import { useMemo, useState } from "react";
import { Link, type To } from "react-router";
import { ChevronRight, Info, Lock } from "lucide-react";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import type { CustomerSpendReport } from "@shared-core/data/customer-api-types";
import {
  formatShare,
  isOnlyUnrecorded,
  shapeBreakdown,
  windowBreakdown,
  type SpendBreakdownKind,
  type SpendBreakdownLine,
  type SpendBreakdownView,
} from "@shared-core/domain/customerSpend";
import { Badge } from "../../../app/components/primitives/badge";
import { Button } from "../../../app/components/primitives/button";
import { Card } from "../../../app/components/primitives/card";
import { cn } from "../../../app/components/primitives/utils";
import { SegmentedControl } from "./SegmentedControl";

// Where the money went: one list at a time (site, product, person), sorted by value, each row with its
// share as a bar. One list rather than side-by-side cards because a real customer has dozens of sites; the
// leaders show first and "Show all" opens the rest, with an "other" row so what is shown still adds up.
//
// There is no client-side drill decision here. A row links to its orders only when the server sent a
// `drill_url` (05_views_module_update_spec.md 9b: aggregates follow reporting scope, orders follow order
// scope), so a total can be readable without being openable.

const NOUNS: Record<SpendBreakdownKind, { one: string; many: string }> = {
  site: { one: "site", many: "sites" },
  product: { one: "product", many: "products" },
  person: { one: "person", many: "people" },
};

const TAB_LABEL: Record<SpendBreakdownKind, string> = {
  site: "Site",
  product: "Product",
  person: "Person",
};

interface BreakdownTab {
  kind: SpendBreakdownKind;
  view: SpendBreakdownView;
}

export function SpendBreakdown({
  report,
  buildPortalUrl,
}: {
  report: CustomerSpendReport;
  buildPortalUrl: (path: string) => To;
}) {
  // A tab only earns its place when it can tell rows apart: a customer with one site has nothing to
  // break down by site, and a breakdown by person where nobody is recorded says only "Not recorded".
  const tabs = useMemo<BreakdownTab[]>(() => {
    const candidates: BreakdownTab[] = [
      { kind: "site", view: shapeBreakdown("site", report.by_site) },
      { kind: "product", view: shapeBreakdown("product", report.by_product) },
    ];
    if (report.by_person && !isOnlyUnrecorded(report.by_person)) {
      candidates.push({
        kind: "person",
        view: shapeBreakdown("person", report.by_person),
      });
    }
    return candidates.filter(
      (tab) => tab.view.lines.length > (tab.kind === "site" ? 1 : 0),
    );
  }, [report]);

  const [kind, setKind] = useState<SpendBreakdownKind>("site");
  const [expandedKinds, setExpandedKinds] = useState<
    Partial<Record<SpendBreakdownKind, boolean>>
  >({});

  const active = tabs.find((t) => t.kind === kind) ?? tabs[0];
  if (!active) return null;
  const expanded = expandedKinds[active.kind] === true;
  const windowed = windowBreakdown(active.view, expanded);
  const nouns = NOUNS[active.kind];
  const hasLinks = active.view.withheldCount < active.view.lines.length;

  return (
    <section aria-labelledby="spend-breakdown-heading" className="space-y-3">
      <Card className="gap-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="spend-breakdown-heading" className="font-medium">
              Where it went
            </h2>
            <p className="text-sm text-muted-foreground">
              Orders placed in this period, invoiced or not, biggest first.
            </p>
          </div>
          {tabs.length > 1 && (
            <SegmentedControl
              label="Break down by"
              value={active.kind}
              onChange={setKind}
              options={tabs.map((t) => ({
                id: t.kind,
                label: TAB_LABEL[t.kind],
                count: t.view.lines.length,
              }))}
            />
          )}
        </div>

        <ul
          aria-label={`Spend by ${nouns.one}`}
          className="-mx-2 divide-y divide-border/60"
        >
          {windowed.shown.map((line) => (
            <BreakdownRow
              key={line.key}
              line={line}
              showGate={active.view.isMixedDrill}
              reserveChevron={hasLinks}
              buildPortalUrl={buildPortalUrl}
            />
          ))}
          {windowed.hiddenCount > 0 && (
            <li className="px-2 py-2.5 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-muted-foreground">
                  {windowed.hiddenCount} other {nouns.many}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {formatCurrencyGbp(windowed.hiddenTotal)}
                  <span className="ml-2 inline-block w-9 text-right text-xs">
                    {formatShare(windowed.hiddenShare)}
                  </span>
                  {hasLinks && <span className="ml-1 inline-block w-4" />}
                </span>
              </div>
            </li>
          )}
        </ul>

        {(windowed.hiddenCount > 0 || windowed.canCollapse) && (
          <div>
            <Button
              variant="outline"
              size="sm"
              aria-expanded={expanded}
              onClick={() =>
                setExpandedKinds((current) => ({
                  ...current,
                  [active.kind]: !expanded,
                }))
              }
            >
              {expanded
                ? `Show fewer ${nouns.many}`
                : `Show all ${active.view.lines.length} ${nouns.many}`}
            </Button>
          </div>
        )}
      </Card>

      {active.kind !== "person" && active.view.withheldCount > 0 && (
        <WithheldNote
          withheld={active.view.withheldCount}
          total={active.view.lines.length}
          nouns={nouns}
          isMixed={active.view.isMixedDrill}
        />
      )}
    </section>
  );
}

function BreakdownRow({
  line,
  showGate,
  reserveChevron,
  buildPortalUrl,
}: {
  line: SpendBreakdownLine;
  showGate: boolean;
  reserveChevron: boolean;
  buildPortalUrl: (path: string) => To;
}) {
  const body = (
    <>
      <span className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span
            className={cn(
              "min-w-0 break-words font-medium",
              line.isUnrecorded && "font-normal italic text-muted-foreground",
            )}
          >
            {line.drillUrl && <span className="sr-only">View orders for </span>}
            {line.name}
          </span>
          {line.code && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {line.code}
            </span>
          )}
          {/* A total that isn't a link needs to look deliberate, not broken (brief 11): the marker only
              appears when some rows DO open, so it reads as the exception it is. */}
          {showGate && !line.drillUrl && (
            <Badge variant="neutral" className="gap-1">
              <Lock className="size-3" />
              Total only
            </Badge>
          )}
        </span>
        <span className="flex shrink-0 items-baseline tabular-nums">
          <span className="font-medium">{formatCurrencyGbp(line.total)}</span>
          <span className="ml-2 w-9 text-right text-xs text-muted-foreground">
            {formatShare(line.share)}
          </span>
          {reserveChevron && (
            <span className="ml-1 inline-flex w-4 justify-end self-center">
              {line.drillUrl && (
                <ChevronRight
                  aria-hidden="true"
                  className="size-4 text-muted-foreground group-hover:text-primary"
                />
              )}
            </span>
          )}
        </span>
      </span>
      <span
        aria-hidden="true"
        className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <span
          className="block h-full rounded-full bg-primary/70"
          style={{ width: `${Math.max(line.barFraction * 100, 1)}%` }}
        />
      </span>
    </>
  );

  return (
    <li>
      {line.drillUrl ? (
        <Link
          to={buildPortalUrl(line.drillUrl)}
          className="group block rounded-md px-2 py-2.5 text-sm outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          {body}
        </Link>
      ) : (
        <div className="px-2 py-2.5 text-sm">{body}</div>
      )}
    </li>
  );
}

function WithheldNote({
  withheld,
  total,
  nouns,
  isMixed,
}: {
  withheld: number;
  total: number;
  nouns: { one: string; many: string };
  isMixed: boolean;
}) {
  return (
    <Card className="flex-row gap-3 border-dashed p-4">
      <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="space-y-1 text-sm">
        <p className="font-medium">
          {isMixed
            ? "Some totals can't be opened to individual orders"
            : "These totals can't be opened to individual orders"}
        </p>
        <p className="text-muted-foreground">
          {isMixed
            ? `${withheld} of ${total} ${nouns.many} include orders placed by colleagues, which you aren't set up to see. Those totals are correct; they're marked "Total only" and have no list of orders behind them.`
            : `Every ${nouns.one} total includes orders placed by colleagues, which you aren't set up to see. The figures are correct, but there's no list of orders behind them.`}
        </p>
        <p className="text-muted-foreground">
          Your group administrator can change this.
        </p>
      </div>
    </Card>
  );
}
